import { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Paper, Typography, Alert, Collapse, Stack, Chip, alpha, useTheme } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import UpdateIcon from '@mui/icons-material/Update';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../constants/tableConfigurations';
import { formatDateShort } from '../../utils/dateUtils';
import { useCollections } from '../../hooks/useCollections';
import { useAuth } from '../../hooks';
import { PageContainer } from '../../components';
import { hasuraClient } from '../../utils/graphqlClient';
import { GET_COLLECTIONS_TOTALS_QUERY } from '../../graphql/queries/collections';
import CollectionImportModal from './CollectionImportModal';
import CollectionManageModal from './CollectionManageModal';
import TahsilatTabs from './TahsilatTabs';

const isReminderDue = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return d <= today;
};

const SummaryCard = ({ label, value, color, icon }) => (
  <Paper elevation={0} sx={{ flex: 1, borderRadius: 3, border: `1px solid ${alpha(color, 0.2)}`, overflow: 'hidden', minWidth: 160 }}>
    <Box sx={{ height: 4, bgcolor: color }} />
    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: alpha(color, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
        {icon}
      </Box>
      <Box>
        <Typography fontWeight={800} color={color} sx={{ fontSize: '1.1rem', lineHeight: 1.1 }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: 11 }}>{label}</Typography>
      </Box>
    </Box>
  </Paper>
);

const TahsilatPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();

  const {
    collections,
    loading,
    fetchData,
    page,
    pageSize,
    totalCount,
    setPage,
    setPageSize,
    setExtraWhereCondition,
    refreshTrigger,
  } = useCollections({ paginated: true });
  const [importOpen, setImportOpen] = useState(false);
  const [manageTarget, setManageTarget] = useState(null);
  const [serverTotals, setServerTotals] = useState({ bor: 0, alacak: 0, bakiye: 0, lastImport: null });

  // Aggregate totals across the FULL data set (independent of pagination).
  // Refetches whenever refreshTrigger bumps (e.g. after import / status edit).
  useEffect(() => {
    const where = user?.role === 'ROOT' ? {} : { company_id: { _eq: user?.companyId } };
    hasuraClient
      .query(GET_COLLECTIONS_TOTALS_QUERY, { where })
      .then((res) => {
        const agg = res?.collections_aggregate?.aggregate || {};
        setServerTotals({
          bor: Number(agg.sum?.tl_bor || 0),
          alacak: Number(agg.sum?.tl_alacak || 0),
          bakiye: Number(agg.sum?.tl_bakiye || 0),
          lastImport: agg.max?.import_date || null,
        });
      })
      .catch(() => {
        // Fallback to current page sums silently — error already surfaced via list query.
      });
  }, [user?.role, user?.companyId, refreshTrigger]);

  const getRowSx = useCallback((row) => {
    const hasDue = (row.reminders || []).some(r => isReminderDue(r.reminder_date));
    return hasDue ? { borderLeft: `3px solid ${theme.palette.warning.main}` } : {};
  }, [theme]);

  const totals = serverTotals;
  const lastImport = serverTotals.lastImport;

  const dueCollections = useMemo(() =>
    collections.filter(c => (c.reminders || []).some(r => isReminderDue(r.reminder_date)))
  , [collections]);

  return (
    <PageContainer currentPage={t('tahsilat.title', 'Tahsilat Yönetimi')}>
      <Box sx={{ py: 3, px: 2 }}>

        <Collapse in={dueCollections.length > 0 && !loading}>
          <Alert
            severity="warning"
            icon={<NotificationsActiveIcon />}
            sx={{
              mb: 2.5, borderRadius: 2.5,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
              alignItems: 'flex-start',
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Typography variant="body2" fontWeight={700} mb={1}>
              {dueCollections.length} firma için tahsilat zamanı geldi
            </Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {dueCollections.map(c => (
                <Chip
                  key={c.id}
                  label={c.firm_short_name || c.firm_name}
                  size="small"
                  onClick={() => setManageTarget(c)}
                  sx={{
                    fontWeight: 700, fontSize: 11, cursor: 'pointer',
                    bgcolor: alpha(theme.palette.warning.main, 0.15),
                    color: 'warning.dark',
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                    '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.25) },
                  }}
                />
              ))}
            </Stack>
          </Alert>
        </Collapse>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <SummaryCard label={t('tahsilat.totalBor', 'Toplam Borç')} value={formatCurrency(totals.bor)} color={theme.palette.error.main} icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} />
          <SummaryCard label={t('tahsilat.totalAlacak', 'Toplam Alacak')} value={formatCurrency(totals.alacak)} color={theme.palette.success.main} icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} />
          <SummaryCard label={t('tahsilat.totalBakiye', 'Net Bakiye')} value={formatCurrency(totals.bakiye)} color={theme.palette.primary.main} icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} />
          {lastImport && (
            <SummaryCard label={t('tahsilat.lastImport', 'Son İmport')} value={formatDateShort(lastImport)} color={theme.palette.text.secondary} icon={<UpdateIcon sx={{ fontSize: 18 }} />} />
          )}
        </Box>

        <TahsilatTabs
          collections={collections}
          loading={loading}
          onManage={setManageTarget}
          onImport={() => setImportOpen(true)}
          getRowSx={getRowSx}
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          setExtraWhereCondition={setExtraWhereCondition}
        />

      </Box>

      <CollectionImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={fetchData}
      />

      <CollectionManageModal
        open={!!manageTarget}
        collection={manageTarget}
        onClose={() => setManageTarget(null)}
        onRefresh={fetchData}
      />
    </PageContainer>
  );
};

export default TahsilatPage;
